import { useTimeslotHelpers } from '@/composables/useTimeslotHelpers.ts'
import { computed, ref } from 'vue'
import { useObjectHelpers } from '@/composables/useObjectHelpers.ts'
import { useDeviceStore } from '@/stores/useDeviceStore'

const {
  canAddDeviceTimeSlotAfter,
  getNextDeviceTimeSlot,
  validateTimeSlots,
} = useTimeslotHelpers()

const { deepCloneObject, objectSerializationsAreEqual } = useObjectHelpers()

// Note that we declare this composable's state outside the exported function
// to make the state global over all of this composable's instances.
const state = ref({})

export function useDeviceTimer(device) {
  if (!device) {
    throw new Error('useDeviceTimer used with invalid device: ' + device)
  }
  if (!state.value.hasOwnProperty(device.id)) {
    state.value[device.id] = device
  }

  const timerData = computed({
    get() {
      return state.value[device.id].timer
    },
    set(newTimerData) {
      state.value[device.id].timer = newTimerData
    }
  })

  const originalTimer = ref(deepCloneObject(state.value[device.id].timer))

  const timerChanged = computed(() => {
    return !objectSerializationsAreEqual(timerData.value, originalTimer.value)
  })

  const timerIsActive = computed({
    get() {
      return timerData.value.active
    },
    set(newValue) {
      timerData.value.active = newValue
    },
  })

  const getTimeSlots = (day) => timerData.value[day]
  const getLastTimeSlot = (slots) => {
    return slots.length > 0 ? slots[slots.length - 1] : false
  }


  const canAddTimeSlot = (day) => {
    const allSlots = getTimeSlots(day)
    const lastTimeSlot = getLastTimeSlot(allSlots)
    return canAddDeviceTimeSlotAfter(lastTimeSlot)
  }

  const timeSlotsAreSetForAllDays = computed({
    get() {
      return timerData.value.setForAllDays
    },
    set(newValue) {
      timerData.value.setForAllDays = newValue
    },
  })

  const timeSlotsAreValid = computed(() => timerData.value.isValid)

  const addTimeSlot = (day) => {
    // Add the slot to all days (including 'all') if data.day is all, or just the
    // given day if not.
    let days = [day]
    if (day === 'all') {
      days.push(
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday'
      )
    }

    days.map((day) => {
      const slots = getTimeSlots(day)
      const lastSlot = getLastTimeSlot(slots)
      if (canAddDeviceTimeSlotAfter(lastSlot)) {
        timerData.value[day].push(getNextDeviceTimeSlot(lastSlot))
      } else {
        throw new Error('Cannot add timeslot to ' + day)
      }
    })
  }

  const updateTimeSlot = (day, slotIndex, start, end) => {
    let days = [day]
    if (day === 'all') {
      days.push(
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday'
      )
    }

    days.map((day) => {
      timerData.value[day][slotIndex].start = start
      timerData.value[day][slotIndex].end = end
    })

    if (timeSlotsAreSetForAllDays.value) {
      // We only need to validate a(ny) day in this case.
      day = 'monday'
    }
    timerData.value.isValid = validateTimeSlots(timerData.value[day])
  }

  const removeTimeSlot = (day, slotIndex) => {
    // Update all days (including 'all') if data.day is all.
    let days = [day]
    if (day === 'all') {
      days.push(
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      )
    }

    days.map((day) => {
      timerData.value[day].splice(slotIndex, 1)
    })
  }

  const defaultOnState = computed({
    get() {
      return timerData.value.defaultOnState
    },
    set(newValue) {
      timerData.value.defaultOnState = newValue
    },
  })

  const restoreOriginalTimerValues = () => {
    timerData.value = deepCloneObject(originalTimer.value)
  }

  const saveToBackend = async () => {
    const deviceStore = useDeviceStore()
    await deviceStore.saveDeviceTimer(state.value[device.id])
    originalTimer.value = deepCloneObject(state.value[device.id].timer)
  }

  return {
    getTimeSlots,
    updateTimeSlot,
    removeTimeSlot,
    addTimeSlot,
    canAddTimeSlot,
    restoreOriginalTimerValues,
    timerIsActive,
    timerChanged,
    timeSlotsAreSetForAllDays,
    timeSlotsAreValid,
    defaultOnState,
    saveToBackend,
  }
}
